/**
 * SNAP-2392: removes personal data and credentials from Sentry events and breadcrumbs before
 * they leave the browser. Wired as `beforeSend` / `beforeBreadcrumb` / `beforeSendTransaction`
 * in `src/main.ts`.
 *
 * The backend's `src/config/sentryScrub.ts` has the same scrubbing rules. Change both together.
 */

const REDACTED = '[redacted]';
const MAX_DEPTH = 25;

const EMAIL = /[A-Za-z0-9._%+-]{1,254}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63})+/g;
const BEARER_TOKEN = /\b(Bearer)\s+[A-Za-z0-9._~+/=-]+/gi;
const AUTHORIZATION_HEADER = /\b(Authorization)\s*:[^\n]*/gi;
const COOKIE_HEADER = /\b(Set-Cookie|Cookie)\s*:[^\n]*/gi;

const SECRET_NAMES = 'access_token|refresh_token|id_token|token|client_secret|api_key|password|secret';
const SECRET_PARAM = new RegExp(`(^|[?&;#\\s])(${SECRET_NAMES})=[^&#;\\s]*`, 'gi');
// `code` is too common in plain text ("status code=500") to match after whitespace.
const CODE_PARAM = /(^|[?&;#])(code)=[^&#;\s]*/gi;
const SECRET_JSON = new RegExp(`(["'])(${SECRET_NAMES}|code)\\1(\\s*:\\s*)"[^"]*"`, 'gi');
// Hash-routed SPA paths and the API invite route carry their credential as a path segment.
const CREDENTIAL_PATH = /((?:invited|signup|password-reset|login\/social(?:-2fa)?)\/)(?!:)[^/?#\s"']+/gi;

/** Object keys whose value is dropped wholesale. Matched after removing `_` and `-`, lowercased. */
const SENSITIVE_KEYS = new Set([
  'authorization',
  'cookie',
  'cookies',
  'setcookie',
  'session',
  'sessionid',
  'password',
  'newpassword',
  'oldpassword',
  'passwordhash',
  'twofactorcode',
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'clientsecret',
  'apikey',
  'secret',
  'code',
  'email',
  'ipaddress',
]);

const normalizeKey = (key: string): string => key.toLowerCase().replace(/[_-]/g, '');

export const scrubText = (text: string): string =>
  text
    .replace(EMAIL, '[email]')
    .replace(AUTHORIZATION_HEADER, `$1: ${REDACTED}`)
    .replace(COOKIE_HEADER, `$1: ${REDACTED}`)
    .replace(BEARER_TOKEN, `$1 ${REDACTED}`)
    .replace(SECRET_PARAM, `$1$2=${REDACTED}`)
    .replace(CODE_PARAM, `$1$2=${REDACTED}`)
    .replace(SECRET_JSON, `$1$2$1$3"${REDACTED}"`)
    .replace(CREDENTIAL_PATH, `$1${REDACTED}`);

const scrubValue = (value: unknown, depth: number, ancestors: WeakSet<object>): unknown => {
  if (typeof value === 'string') return scrubText(value);
  if (value === null || typeof value !== 'object' || value instanceof Date) return value;
  if (depth >= MAX_DEPTH) return '[truncated]';
  if (ancestors.has(value)) return '[circular]';

  ancestors.add(value);
  try {
    if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1, ancestors));

    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      out[key] = SENSITIVE_KEYS.has(normalizeKey(key)) ? REDACTED : scrubValue(child, depth + 1, ancestors);
    }
    return out;
  } finally {
    ancestors.delete(value);
  }
};

const scrubTree = <T>(value: T): T => scrubValue(value, 0, new WeakSet()) as T;

export const scrubSentryEvent = <T>(event: T): T => scrubTree(event);

export const scrubSentryBreadcrumb = <T>(breadcrumb: T): T => scrubTree(breadcrumb);
